import { useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Share2, Copy, Check, Layers, BadgeCheck } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { trackEvent } from "@/lib/analytics";
import lifemartLogo from "@/assets/lp/lifemart-logo.svg";

interface Props {
  totalEstimated: number;
  zip: string;
  state?: string | null;
  /** Co-brand the share card and list program names when the viewer is a LifeMart member. */
  lifemart?: boolean;
  programNames?: string[];
  /** How many programs the stack returned. Used for the headline when the
   *  matched programs publish no dollar figures (a $0 total is common). */
  programCount?: number;
}

function fmt(n: number) {
  if (n >= 1000) return `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return `$${n}`;
}

// Mask exact ZIP to a region (first 3 digits) so users aren't oversharing.
function regionFromZip(zip: string, state?: string | null) {
  const prefix = zip.slice(0, 3);
  return state ? `${state} (${prefix}xx)` : `${prefix}xx region`;
}

/**
 * Branded "I just stacked $X" card.
 * Uses the Web Share API where available, falls back to clipboard copy.
 */
export default function StackShareCard({ totalEstimated, zip, state, lifemart = false, programNames = [], programCount = 0 }: Props) {
  const hasAmount = totalEstimated > 0;
  const countLabel = `${programCount} assistance program${programCount === 1 ? "" : "s"}`;
  const headlineValue = hasAmount ? fmt(totalEstimated) : String(programCount);
  const cardRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  const region = regionFromZip(zip, state);
  const shareText = lifemart
    ? `LifeMart unlocked ${hasAmount ? fmt(totalEstimated) : countLabel} toward my down payment through Home Approach — every federal, state, county, and employer program, named. See yours:`
    : `I just found ${hasAmount ? `${fmt(totalEstimated)} toward my down payment` : countLabel} with Home Approach — federal + state + county + employer assistance, all in one. See yours:`;
  const shareUrl = lifemart
    ? "https://homeapproach.com/lifemart?ref=share"
    : "https://homeapproach.com/stack?ref=share";

  const handleShare = async () => {
    trackEvent("stack_share_click", { method: "native", total: totalEstimated });
    if (navigator.share) {
      try {
        await navigator.share({ title: "My Stack", text: shareText, url: shareUrl });
        return;
      } catch {
        // user cancelled — fall through to copy
      }
    }
    await handleCopy();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      trackEvent("stack_share_copy", { total: totalEstimated });
      toast({ title: "Copied!", description: "Share it with a friend to earn referral points." });
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast({ title: "Copy failed", description: "Long-press to copy manually.", variant: "destructive" });
    }
  };

  return (
    <Card className="overflow-hidden">
      <div
        ref={cardRef}
        className="relative p-6 sm:p-8 text-white"
        style={{
          background:
            "linear-gradient(135deg, #36436D 0%, #4a5a8a 55%, #B06A50 100%)",
        }}
      >
        {lifemart && (
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em]">
              <BadgeCheck className="h-3 w-3" /> Unlocked via LifeMart
            </span>
            <span className="inline-flex items-center rounded bg-white/95 px-1.5 py-0.5">
              <img src={lifemartLogo} alt="LifeMart" className="h-3.5 w-auto" />
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 mb-3 opacity-90">
          <Layers className="h-4 w-4" />
          <span className="text-xs uppercase tracking-[0.18em] font-semibold">Stack · Home Approach</span>
        </div>
        <p className="text-sm opacity-90 mb-1">{hasAmount ? "I just stacked" : "I just found"}</p>
        <div className="text-5xl sm:text-6xl font-bold mb-2" style={{ fontFamily: "var(--heading-font)" }}>
          {headlineValue}
        </div>
        <p className="text-sm opacity-90 mb-4">
          {hasAmount
            ? `in potential down-payment assistance for the ${region}.`
            : `homebuyer assistance program${programCount === 1 ? "" : "s"} I may qualify for in the ${region}.`}
        </p>
        <div className="flex gap-2 text-[10px] uppercase tracking-wider opacity-80">
          <span className="px-2 py-0.5 rounded bg-white/15">Federal</span>
          <span className="px-2 py-0.5 rounded bg-white/15">State</span>
          <span className="px-2 py-0.5 rounded bg-white/15">County</span>
          <span className="px-2 py-0.5 rounded bg-white/15">Employer</span>
        </div>
        {lifemart && programNames.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/20">
            <p className="text-[10px] uppercase tracking-[0.18em] opacity-75 mb-2">Programs in this stack</p>
            <ul className="flex flex-wrap gap-1.5">
              {programNames.slice(0, 8).map((n) => (
                <li key={n} className="text-[11px] px-2 py-0.5 rounded bg-white/10">
                  {n}
                </li>
              ))}
              {programNames.length > 8 && (
                <li className="text-[11px] px-2 py-0.5 rounded bg-white/10">+{programNames.length - 8} more</li>
              )}
            </ul>
          </div>
        )}
      </div>

      <div className="p-4 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center justify-between bg-muted/30">
        <p className="text-xs text-muted-foreground">
          Share your stack — every friend who runs theirs earns you referral points.
        </p>
        <div className="flex gap-2">
          <Button onClick={handleCopy} variant="outline" size="sm" className="gap-2">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy"}
          </Button>
          <Button onClick={handleShare} size="sm" className="gap-2">
            <Share2 className="h-4 w-4" /> Share my Stack
          </Button>
        </div>
      </div>
    </Card>
  );
}